import Vue from 'vue';
import Vuex from 'vuex';
import assistPage from './modules/assistPage';
import appSettings from './modules/appSettings';
import auth from './modules/auth';
import chapter from './modules/chapter';
import sponsor from './modules/sponsor';
import myAccount from './modules/myAccount';
import forum from './modules/forum';
import forumMeetings from './modules/forumMeetings';
import network from './modules/network';
import group from './modules/group';
import speaker from './modules/speaker';
import searchSpeaker from './modules/searchSpeaker';
import messenger from './modules/messenger';
import memberProfile from './modules/memberProfile';
import memberDirectory from './modules/memberDirectory';
import contentDirectory from './modules/contentDirectory';
import notification from './modules/notification';
import communityDirectory from './modules/communityDirectory';
import chapterWaiver from './modules/chapterWaiver';
import ypoSearch from "./modules/ypoSearch";
import chapterEvents from './modules/chapterEvents';
import champsEvents from './modules/champsEvents';
import tools from './modules/tools';
import actionState from './modules/actionState';
import { storeGetters } from './storeGetters.constant';
import { storeMutation } from './storeMutations.constant';
import { userRoles, routerNames } from '../components/appCommon.constants';
import { appConstant } from '../app.constant';
import feedback from './modules/feedback';
import businesses from './modules/businesses';
import eventAttendees from './modules/eventAttendees';
import policiesProcedures from './modules/policiesProcedures';
import champsPage from './modules/champsPage';
import champsEmail from './modules/champsEmail';
import ypoCommon from './modules/ypoCommon';
import newsFeed from './modules/newsFeed';
import openAPI from './modules/openAPI';
import ypoError from "@/store/modules/ypoError";
import ypoEvents from "@/store/modules/ypoEvents";
import ypoConciergeService from "@/store/modules/ypoConcierge";
import ypoResources from "./modules/ypoResources";
import manageLoginSecurity from "./modules/manageLoginSecurity";
import connectPlus from "@/store/modules/connectPlus";
import snackbar from './modules/snackbar';
import chapterStudio from "@/store/modules/chapterStudio";
import manageChapterStudio from "@/store/modules/manageChapterStudio";

Vue.use(Vuex);

const rootState = {
    sessionExpiryNotice: false,
    routeParams: {},
    routeName: '',
    toggleMainPageOverflow: false,
    currentCommunityView: userRoles.MEMBER,
    salesforceURL: process.env.VUE_APP_SALESFORCE_URL,
    salesforceURLRoot: process.env.VUE_APP_SALESFORCE_URL_ROOT,
    cventURL: process.env.VUE_APP_CVENT_URL,
    paymentRedirectUrl: process.env.VUE_APP_PAYMENT_REDIRECT_URL,
    mobileAppBrowser:
        navigator.userAgent.toLowerCase().includes(appConstant.YPO_IOS_CONNECT.toLowerCase()) ||
        navigator.userAgent.toLowerCase().includes(appConstant.YPO_ANDROID_CONNECT.toLowerCase()),
    androidAppBrowser: navigator.userAgent.toLowerCase().includes(appConstant.YPO_ANDROID_CONNECT.toLowerCase()),
    iosAppBrowser: navigator.userAgent.toLowerCase().includes(appConstant.YPO_IOS_CONNECT.toLowerCase()),
    cancelableRequests: {},
    activityFeedCancelableRequests: {},
    inactiveSessionDetected: false,
    paramConfig: {
        initialized: false,
        hideHeader: false,
        hideFooter: false,
        engagementApp: false,
        hideConcierge: false,
    },
    coreContentLoadingInProgress: false,
};

const rootGetters = {
    [storeGetters.forumPromise]: (state) => {
        if (!state.forum.forumTokenPromise) {
            return Promise.resolve(null);
        }

        return state.forum.forumTokenPromise.then((response) => {
            return response;
        });
    },
    [storeGetters.communityIdPromise]: async (state) => {
        // Route parameters with the name communityId take precedence over the default.
        if (state.routeParams.communityId) {
            return Promise.resolve(state.routeParams.communityId);
        }

        if (state.routeName.toLowerCase() === routerNames.BUSINESSES.toLowerCase()) {
            return Promise.resolve(state.chapter.businessCommunityId);
        }
        return Promise.resolve(null);
    },
    [storeGetters.isMobileAppBrowser]: (state) => {
        return state.mobileAppBrowser;
    },
    [storeGetters.isIosAppBrowser]: (state) => {
        return state.iosAppBrowser;
    },
    [storeGetters.isAndroidAppBrowser]: (state) => {
        return state.androidAppBrowser;
    },
    [storeGetters.communityName]: (state) => {
        if (state.routeName === routerNames.CHAMPS_PAGE) {
            return routerNames.CHAPTER;
        }
        return state.routeName;
    },
    [storeGetters.networkTypePromise]: () => {
        return Promise.resolve(); // TODO inspect communities
    },
    [storeGetters.getMainPageOverflow]: state => state.toggleMainPageOverflow,
    [storeGetters.currentCommunityView]: state => state.currentCommunityView,
    [storeGetters.salesforceURL]: state => state.salesforceURL,
    [storeGetters.salesforceURLRoot]: state => state.salesforceURLRoot,
    [storeGetters.cventURL]: state => state.cventURL,
    [storeGetters.paymentRedirectUrl]: state => state.paymentRedirectUrl,
    [storeGetters.sessionExpiry]: state => state.sessionExpiryNotice,
    [storeGetters.inactiveSessionDetected]: state => state.inactiveSessionDetected,
    [storeGetters.routeConfigHideHeader]: state => state.paramConfig.hideHeader,
    [storeGetters.routeConfigHideFooter]: state => state.paramConfig.hideFooter,
    [storeGetters.routeConfigEngagementApp]: state => state.paramConfig.engagementApp,
    [storeGetters.routeConfigHideConcierge]: state => state.paramConfig.hideConcierge,
    [storeGetters.isCoreContentLoadingInProgress]: state => state.coreContentLoadingInProgress,

};
const stateMutation = {
    [storeMutation.setInactiveSessionDetection]: (state, data) => {
        state.inactiveSessionDetected = data || false;
    },
    [storeMutation.setSessionExpiryNotice]: (state, data) => {
        state.sessionExpiryNotice = data || false;
    },
    [storeMutation.toggleMainPageOverflow]: (state) => {
        state.toggleMainPageOverflow = !state.toggleMainPageOverflow;
    },
    [storeMutation.setCurrentCommunityView]: (state, data) => {
        state.currentCommunityView = data;
    },
    [storeMutation.handleCancelableRequest]: (state, requestObject) => {
        const { actionName, requestSource } = requestObject;
        if (state.cancelableRequests.hasOwnProperty(actionName)) {
            state.cancelableRequests[actionName].cancel(`${actionName} Request canceled`);
        }

        state.cancelableRequests = {
            ...state.cancelableRequests,
            [actionName]: requestSource,
        };
    },
    [storeMutation.removeCancelableRequest]: (state, actionName) => {
        if (state.cancelableRequests && actionName && state.cancelableRequests.hasOwnProperty(actionName)) {
            delete state.cancelableRequests[actionName];
        }
    },
    [storeMutation.handleActivityFeedCancelableRequest]: (state, requestObject) => {
        const {
             communityId, actionName, requestName, requestSource,
             } = requestObject;

        if (state.activityFeedCancelableRequests.communityId !== communityId) {
            if (
                state.activityFeedCancelableRequests.communityId ||
                state.activityFeedCancelableRequests.communityId === null
            ) {
                Object.keys(state.activityFeedCancelableRequests.requests).forEach((key) => {
                    state.activityFeedCancelableRequests.requests[key].cancel();
                });
                Object.keys(state.activityFeedCancelableRequests.requests).forEach((key) => {
                    delete state.activityFeedCancelableRequests.requests[key];
                });
            }
        }

        if (
            state.activityFeedCancelableRequests.requests &&
            state.activityFeedCancelableRequests.requests.hasOwnProperty(requestName)
        ) {
            state.activityFeedCancelableRequests.requests[requestName].cancel(`${actionName} Request canceled`);
        }
        state.activityFeedCancelableRequests.communityId = communityId;
        state.activityFeedCancelableRequests.requests = {
            ...state.activityFeedCancelableRequests.requests,
            [requestName]: requestSource,
        };
    },
    [storeMutation.removeActivityFeedCancelableRequest]: (state, requestName) => {
        if (
            requestName &&
            state.activityFeedCancelableRequests &&
            state.activityFeedCancelableRequests.requests &&
            state.activityFeedCancelableRequests.requests.hasOwnProperty(requestName)
        ) {
            delete state.activityFeedCancelableRequests.requests[requestName];
        }
    },
    [storeMutation.handleParamConfig]: (state, routeParams) => {
        if (routeParams && !state.paramConfig.initialized) {
            state.paramConfig = {
                ...state.paramConfig,
                initialized: !!(routeParams.hideHeader || routeParams.hideFooter || routeParams.engagementApp || routeParams.hideConcierge),
                hideHeader: routeParams.hideHeader && routeParams.hideHeader === "true",
                hideFooter: routeParams.hideFooter && routeParams.hideFooter === "true",
                engagementApp: routeParams.engagementApp && routeParams.engagementApp === "true",
                hideConcierge: routeParams.hideConcierge && routeParams.hideConcierge === "true",
            };
        }
    },
    [storeMutation.setCoreConfigLoadingState]: (state, value) => {
        state.coreContentLoadingInProgress = value;
    },
};

export default new Vuex.Store({
    modules: {
        appSettings,
        auth,
        chapter,
        sponsor,
        actionState,
        myAccount,
        tools,
        memberProfile,
        memberDirectory,
        contentDirectory,
        notification,
        communityDirectory,
        ypoSearch,
        eventAttendees,
        forum,
        forumMeetings,
        network,
        group,
        speaker,
        searchSpeaker,
        messenger,
        assistPage,
        feedback,
        chapterWaiver,
        businesses,
        chapterEvents,
        policiesProcedures,
        champsPage,
        champsEmail,
        ypoCommon,
        champsEvents,
        newsFeed,
        openAPI,
        ypoError,
        ypoEvents,
        ypoConciergeService,
        ypoResources,
        manageLoginSecurity,
        connectPlus,
        snackbar,
        chapterStudio,
        manageChapterStudio,
    },
    state: { ...rootState },
    mutations: { ...stateMutation },
    actions: {},
    getters: { ...rootGetters },
});
